// FT-ANALYTICS: per-portal dashboard. Fetches /api/portals/{slug}/analytics
// and shows total views, total clicks, top links, and top referrers
// for the last 30 days. Pro-only — the API gates and we surface a
// simple UpgradePrompt on 403.

import { useEffect, useState } from "preact/hooks";
import { UpgradePrompt } from "./UpgradePrompt";
import { UserMenu } from "./UserMenu";
import { useNavigate } from "../contexts/AppContext";

type TopLink = { block_id: string; label: string; clicks: number };
type TopRef = { host: string; count: number };
type Agg = {
  views_total: number;
  clicks_total: number;
  top_links: TopLink[] | null;
  top_referrers: TopRef[] | null;
};

export function Analytics({ id }: { id: string }) {
  const navigate = useNavigate();
  const [agg, setAgg] = useState<Agg | null>(null);
  const [error, setError] = useState<"forbidden" | "other" | null>(null);

  useEffect(() => {
    fetch(`/api/portals/${encodeURIComponent(id)}/analytics`)
      .then((r) => {
        if (r.status === 403) {
          setError("forbidden");
          return null;
        }
        if (!r.ok) {
          setError("other");
          return null;
        }
        return r.json();
      })
      .then((d) => d && setAgg(d))
      .catch(() => setError("other"));
  }, [id]);

  return (
    <div class="container py-5" style="max-width: 720px">
      <div class="d-flex flex-column gap-3 mb-4">
        <div class="d-flex align-items-center gap-3">
          <button
            type="button"
            class="btn btn-link p-0 text-decoration-none"
            onClick={() => navigate(`/edit/${id}`)}
          >
            ← Editor
          </button>
          <h1 class="h4 m-0">Analytics — {id}</h1>
        </div>
        <UserMenu />
      </div>

      {error === "forbidden" && (
        <UpgradePrompt reason="Analytics is a Pro feature." />
      )}
      {error === "other" && (
        <div class="alert alert-danger">Couldn't load analytics.</div>
      )}
      {!error && !agg && <div class="text-muted">Loading…</div>}
      {agg && (
        <>
          <div class="row g-3 mb-4">
            <div class="col">
              <div class="card">
                <div class="card-body">
                  <div class="text-muted small text-uppercase">Views (30d)</div>
                  <div class="h3 mb-0">{agg.views_total}</div>
                </div>
              </div>
            </div>
            <div class="col">
              <div class="card">
                <div class="card-body">
                  <div class="text-muted small text-uppercase">
                    Clicks (30d)
                  </div>
                  <div class="h3 mb-0">{agg.clicks_total}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <div class="text-muted small text-uppercase mb-2">Top links</div>
              {(agg.top_links || []).length === 0 && (
                <div class="text-muted small">No clicks yet.</div>
              )}
              {(agg.top_links || []).map((l) => (
                <div
                  key={l.block_id}
                  class="d-flex justify-content-between py-1"
                >
                  <span>{l.label || l.block_id}</span>
                  <span class="text-muted">{l.clicks}</span>
                </div>
              ))}
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <div class="text-muted small text-uppercase mb-2">
                Top referrers
              </div>
              {(agg.top_referrers || []).length === 0 && (
                <div class="text-muted small">No referrers yet.</div>
              )}
              {(agg.top_referrers || []).map((r) => (
                <div key={r.host} class="d-flex justify-content-between py-1">
                  <span>{r.host}</span>
                  <span class="text-muted">{r.count}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
